/**
 * M9.1：国家表（`country` 列用）。
 *
 * ISO 3166-1 alpha-2 码表来自逆向产物 `monday-clone/evidence/column-constants.json`
 * （monday 的 COUNTRY_COLUMN_CONSTANTS，共 249 个）。中文显示名不内置，
 * 用浏览器内置的 Intl.DisplayNames 现场生成（零体积、随系统语言数据更新）。
 */

export const COUNTRY_CODES: string[] = [
  'AC', 'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS',
  'AT', 'AU', 'AW', 'AX', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH',
  'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW',
  'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM',
  'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK',
  'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ',
  'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI',
  'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK',
  'HM', 'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ',
  'IR', 'IS', 'IT', 'JE', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM',
  'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR',
  'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH',
  'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV',
  'MW', 'MX', 'MY', 'MZ', 'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO',
  'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL',
  'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM',
  'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF',
  'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW',
  'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'XK', 'YE', 'ZA', 'ZM', 'ZW',
]

type DisplayNamesCtor = new (
  locales: string[],
  options: { type: 'region' },
) => { of: (code: string) => string | undefined }

let displayNames: { of: (code: string) => string | undefined } | null = null
try {
  const Ctor = (Intl as unknown as { DisplayNames?: DisplayNamesCtor }).DisplayNames
  if (Ctor !== undefined) displayNames = new Ctor(['zh-CN'], { type: 'region' })
} catch {
  displayNames = null
}

/** ISO 码 -> 中文国家名（取不到就回退成码本身） */
export function countryName(code: string): string {
  const c = (code ?? '').trim().toUpperCase()
  // 只接受两位字母的 ISO 3166-1 alpha-2；其它（空、乱数据、'1'、长 JSON）一律原样返回。
  // 注意：Intl.DisplayNames.of() 对非法地区码会抛 RangeError，一旦在渲染里抛出就是整页白屏。
  if (!/^[A-Z]{2}$/.test(c)) return c
  if (displayNames !== null) {
    try {
      const n = displayNames.of(c)
      if (n !== undefined && n !== '' && n !== c) return n
    } catch {
      return c
    }
  }
  return c
}

/** 按「中文名 / ISO 码」模糊搜索，最多返回 limit 个 */
export function searchCountries(query: string, limit = 30): string[] {
  const q = query.trim().toLowerCase()
  if (q === '') return COUNTRY_CODES.slice(0, limit)
  const hit: string[] = []
  for (const code of COUNTRY_CODES) {
    const name = countryName(code).toLowerCase()
    if (name.includes(q) || code.toLowerCase().includes(q)) {
      hit.push(code)
      if (hit.length >= limit) break
    }
  }
  return hit
}